import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PaymentViewOperation,
  getPaymentViewOperations,
} from '../services/api';
import {
  SortState,
  compareValues,
  formatContractSumDisplay,
  formatDateShort,
  toggleSort,
} from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import '../styles/DataPage.css';

type SortKey =
  | 'operation_date'
  | 'account_name'
  | 'account_type'
  | 'contractor_name_pf'
  | 'category_name'
  | 'project_name'
  | 'task_name'
  | 'contract_number'
  | 'comment'
  | 'amount';

type FilterMenuKey =
  | 'account'
  | 'currency'
  | 'contractor'
  | 'category'
  | 'project'
  | 'task'
  | 'deal'
  | null;

function contractLabel(row: PaymentViewOperation): string {
  return (row.contract_number || row.deal_name || '').trim();
}

function parseAmountBound(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function parseFilterDay(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const ms = new Date(`${v}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function dateInRange(value: string, from: string, to: string): boolean {
  const hasFrom = from.trim() !== '';
  const hasTo = to.trim() !== '';
  if (!hasFrom && !hasTo) return true;

  const day = parseFilterDay(value);
  if (day === null) return true;

  let rangeStart = hasFrom ? parseFilterDay(from) : Number.NEGATIVE_INFINITY;
  let rangeEnd = hasTo ? parseFilterDay(to) : Number.POSITIVE_INFINITY;
  if (rangeStart === null) rangeStart = Number.NEGATIVE_INFINITY;
  if (rangeEnd === null) rangeEnd = Number.POSITIVE_INFINITY;
  return day >= rangeStart && day <= rangeEnd;
}

const PaymentViewPage: React.FC = () => {
  const { can } = useAuth();
  const [operations, setOperations] = useState<PaymentViewOperation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<FilterMenuKey>(null);
  const [searchText, setSearchText] = useState('');

  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterAccounts, setFilterAccounts] = useState<string[]>([]);
  const [filterCurrencies, setFilterCurrencies] = useState<string[]>([]);
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [filterProjects, setFilterProjects] = useState<string[]>([]);
  const [filterTaskNames, setFilterTaskNames] = useState<string[]>([]);
  const [filterDeals, setFilterDeals] = useState<string[]>([]);
  const [filterContractors, setFilterContractors] = useState<string[]>([]);
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');

  const [sortState, setSortState] = useState<SortState<SortKey>>({
    key: 'operation_date',
    direction: 'desc',
  });

  const canView = can('INCOME', 'VIEW');

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canView) {
      setIsLoading(false);
      setOperations([]);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const data = await getPaymentViewOperations();
      setOperations(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка при загрузке поступлений';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    if (canView) {
      void fetchData();
    } else {
      setIsLoading(false);
      setOperations([]);
    }
  }, [canView, fetchData]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const uniqueValues = useCallback(
    (picker: (row: PaymentViewOperation) => string | undefined): string[] => {
      const set = new Set<string>();
      for (const row of operations) {
        const value = picker(row)?.trim();
        if (value) {
          set.add(value);
        }
      }
      return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
    },
    [operations],
  );

  const accountOptions = useMemo(() => uniqueValues((row) => row.account_name), [uniqueValues]);
  const currencyOptions = useMemo(() => uniqueValues((row) => row.account_type), [uniqueValues]);
  const categoryOptions = useMemo(() => uniqueValues((row) => row.category_name), [uniqueValues]);
  const projectOptions = useMemo(() => uniqueValues((row) => row.project_name), [uniqueValues]);
  const taskNameOptions = useMemo(() => uniqueValues((row) => row.task_name), [uniqueValues]);
  const dealOptions = useMemo(() => uniqueValues((row) => contractLabel(row)), [uniqueValues]);
  const contractorOptions = useMemo(() => uniqueValues((row) => row.contractor_name_pf), [uniqueValues]);

  const hasActiveFilters = useMemo(
    () =>
      filterDateFrom.trim() !== ''
      || filterDateTo.trim() !== ''
      || filterAccounts.length > 0
      || filterCurrencies.length > 0
      || filterCategories.length > 0
      || filterProjects.length > 0
      || filterTaskNames.length > 0
      || filterDeals.length > 0
      || filterContractors.length > 0
      || minAmountFilter.trim() !== ''
      || maxAmountFilter.trim() !== '',
    [
      filterDateFrom,
      filterDateTo,
      filterAccounts,
      filterCurrencies,
      filterCategories,
      filterProjects,
      filterTaskNames,
      filterDeals,
      filterContractors,
      minAmountFilter,
      maxAmountFilter,
    ],
  );

  const clearFilters = (): void => {
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterAccounts([]);
    setFilterCurrencies([]);
    setFilterCategories([]);
    setFilterProjects([]);
    setFilterTaskNames([]);
    setFilterDeals([]);
    setFilterContractors([]);
    setMinAmountFilter('');
    setMaxAmountFilter('');
  };

  const filteredOperations = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    const sumFromN = parseAmountBound(minAmountFilter);
    const sumToN = parseAmountBound(maxAmountFilter);

    return operations.filter((row) => {
      const deal = contractLabel(row);
      const searchable = [
        row.operation_date,
        row.account_name,
        row.account_type,
        row.category_name,
        row.project_name,
        row.task_name,
        deal,
        row.contractor_name_pf,
        row.comment,
        String(row.amount),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const amount = row.amount;

      return (
        (!text || searchable.includes(text))
        && dateInRange(row.operation_date, filterDateFrom, filterDateTo)
        && (filterAccounts.length === 0 || (row.account_name && filterAccounts.includes(row.account_name)))
        && (filterCurrencies.length === 0 || (row.account_type && filterCurrencies.includes(row.account_type)))
        && (filterCategories.length === 0 || (row.category_name && filterCategories.includes(row.category_name)))
        && (filterProjects.length === 0 || (row.project_name && filterProjects.includes(row.project_name)))
        && (filterTaskNames.length === 0 || (row.task_name && filterTaskNames.includes(row.task_name)))
        && (filterDeals.length === 0 || (deal && filterDeals.includes(deal)))
        && (filterContractors.length === 0 || (row.contractor_name_pf && filterContractors.includes(row.contractor_name_pf)))
        && (sumFromN === null || amount >= sumFromN)
        && (sumToN === null || amount <= sumToN)
      );
    });
  }, [
    operations,
    searchText,
    filterDateFrom,
    filterDateTo,
    filterAccounts,
    filterCurrencies,
    filterCategories,
    filterProjects,
    filterTaskNames,
    filterDeals,
    filterContractors,
    minAmountFilter,
    maxAmountFilter,
  ]);

  const sortedOperations = useMemo(() => {
    const sorted = [...filteredOperations];
    sorted.sort((left, right) => {
      const leftMap: Record<SortKey, string | number | undefined> = {
        operation_date: left.operation_date,
        account_name: left.account_name,
        account_type: left.account_type,
        contractor_name_pf: left.contractor_name_pf,
        category_name: left.category_name,
        project_name: left.project_name,
        task_name: left.task_name,
        contract_number: contractLabel(left),
        comment: left.comment,
        amount: left.amount,
      };
      const rightMap: Record<SortKey, string | number | undefined> = {
        operation_date: right.operation_date,
        account_name: right.account_name,
        account_type: right.account_type,
        contractor_name_pf: right.contractor_name_pf,
        category_name: right.category_name,
        project_name: right.project_name,
        task_name: right.task_name,
        contract_number: contractLabel(right),
        comment: right.comment,
        amount: right.amount,
      };
      return compareValues(leftMap[sortState.key], rightMap[sortState.key], sortState.direction);
    });
    return sorted;
  }, [filteredOperations, sortState]);

  const totalFilteredAmount = useMemo(
    () => sortedOperations.reduce((sum, row) => sum + row.amount, 0),
    [sortedOperations],
  );

  const handleSort = (key: SortKey): void => {
    setSortState((current) => toggleSort(
      current,
      key,
      key === 'operation_date' || key === 'amount' ? 'desc' : 'asc',
    ));
  };

  const renderSortableHeader = (
    key: SortKey,
    label: string,
    className = '',
  ): React.ReactNode => (
    <th
      className={`sortable ${className}`.trim()}
      onClick={() => handleSort(key)}
      role="columnheader"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleSort(key);
        }
      }}
      aria-sort={sortState.key === key ? (sortState.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {sortState.key === key ? (sortState.direction === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  return (
    <div className="data-page operations-page">
      {!canView ? (
        <div className="empty-state">Нет доступа (требуется INCOME VIEW).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Поступления</h1>
            <div className="page-header-row__actions">
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error ? <div className="error-message">{error}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen ? (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Счёт"
                      options={accountOptions}
                      selected={filterAccounts}
                      onSelectedChange={setFilterAccounts}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'account'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'account' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Валюта"
                      options={currencyOptions}
                      selected={filterCurrencies}
                      onSelectedChange={setFilterCurrencies}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'currency'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'currency' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Контрагент"
                      options={contractorOptions}
                      selected={filterContractors}
                      onSelectedChange={setFilterContractors}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'contractor'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contractor' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статья"
                      options={categoryOptions}
                      selected={filterCategories}
                      onSelectedChange={setFilterCategories}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'category'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'category' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Задача"
                      options={projectOptions}
                      selected={filterProjects}
                      onSelectedChange={setFilterProjects}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'project'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'project' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Название"
                      options={taskNameOptions}
                      selected={filterTaskNames}
                      onSelectedChange={setFilterTaskNames}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'task'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'task' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Договор"
                      options={dealOptions}
                      selected={filterDeals}
                      onSelectedChange={setFilterDeals}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'deal'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'deal' : null)}
                    />
                  </div>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Дата</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                      />
                    </div>
                  </label>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Сумма</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="filters-sum-block__input"
                        placeholder="от"
                        value={minAmountFilter}
                        onChange={(e) => setMinAmountFilter(e.target.value)}
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        className="filters-sum-block__input"
                        placeholder="до"
                        value={maxAmountFilter}
                        onChange={(e) => setMaxAmountFilter(e.target.value)}
                      />
                    </div>
                  </label>
                </div>
              </aside>
            ) : null}

            <section className="table-content">
              {isLoading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table operations-page__table">
                      <colgroup>
                        <col className="operations-page__colgrid operations-page__colgrid--date" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--account-type" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--category" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--comment" />
                        <col className="operations-page__colgrid operations-page__colgrid--sum" />
                      </colgroup>
                      <thead>
                        <tr>
                          {renderSortableHeader('operation_date', 'Дата', 'operations-page__th-date')}
                          {renderSortableHeader('account_name', 'Счёт')}
                          {renderSortableHeader('account_type', 'Валюта', 'operations-page__th-account-type')}
                          {renderSortableHeader('contractor_name_pf', 'Контрагент')}
                          {renderSortableHeader('task_name', 'Название')}
                          {renderSortableHeader('project_name', 'Задача')}
                          {renderSortableHeader('category_name', 'Статья')}
                          {renderSortableHeader('contract_number', 'Договор')}
                          {renderSortableHeader('comment', 'Комментарий')}
                          {renderSortableHeader('amount', 'Сумма', 'col-num operations-page__th-sum')}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedOperations.length === 0 ? (
                          <tr>
                            <td colSpan={10} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedOperations.map((row) => (
                            <tr key={row.id}>
                              <td className="operations-page__td-date">{formatDateShort(row.operation_date)}</td>
                              <td><div className="operations-page__clamp-2">{row.account_name || '—'}</div></td>
                              <td className="operations-page__td-account-type">{row.account_type || '—'}</td>
                              <td><div className="operations-page__clamp-2">{row.contractor_name_pf || '—'}</div></td>
                              <td><div className="operations-page__clamp-2">{row.task_name || '—'}</div></td>
                              <td><div className="operations-page__clamp-2">{row.project_name || '—'}</div></td>
                              <td><div className="operations-page__clamp-2">{row.category_name || '—'}</div></td>
                              <td><div className="operations-page__clamp-2">{contractLabel(row) || '—'}</div></td>
                              <td><div className="operations-page__clamp-2">{row.comment || '—'}</div></td>
                              <td className="col-num operations-page__td-sum">
                                {formatContractSumDisplay(row.amount)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer operations-page__table">
                      <colgroup>
                        <col className="operations-page__colgrid operations-page__colgrid--date" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--account-type" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--category" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--comment" />
                        <col className="operations-page__colgrid operations-page__colgrid--sum" />
                      </colgroup>
                      <tfoot>
                        <tr>
                          <td>Итого: {sortedOperations.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td className="col-num operations-page__td-sum">
                            {formatContractSumDisplay(totalFilteredAmount)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentViewPage;
